import type { HTMLAttributes, ReactNode } from 'react';
import { Icon } from '../../lib/icons.js';

/** A pane of glass. `raised` for elements that sit above other glass (sheets, menus). */
export function Panel({
  children,
  title,
  icon,
  action,
  className = '',
  raised = false,
  as: As = 'section',
  ...rest
}: {
  children?: ReactNode;
  title?: ReactNode;
  icon?: string;
  action?: ReactNode;
  className?: string;
  raised?: boolean;
  as?: 'section' | 'div' | 'article';
} & Omit<HTMLAttributes<HTMLElement>, 'title'>): ReactNode {
  return (
    <As className={`glass panel ${raised ? 'glass-raised' : ''} ${className}`} {...rest}>
      {(title || action) && (
        <header className="panel-title">
          {icon && <Icon name={icon} size={14} strokeWidth={1.9} />}
          {title && <span>{title}</span>}
          {action && (
            <>
              <span className="spacer" />
              {action}
            </>
          )}
        </header>
      )}
      {children}
    </As>
  );
}

/** Section heading used between groups of tiles. */
export function SectionHeader({
  title,
  icon,
  meta,
  action,
  id,
}: {
  title: ReactNode;
  icon?: string;
  meta?: ReactNode;
  action?: ReactNode;
  id?: string;
}): ReactNode {
  return (
    <div className="section-head" {...(id ? { id } : {})}>
      {icon && <Icon name={icon} size={15} strokeWidth={1.9} />}
      <h2>{title}</h2>
      {meta && <span className="section-meta">{meta}</span>}
      <span className="spacer" />
      {action}
    </div>
  );
}

/**
 * Empty / error state. `action` adds a control under the text (e.g. a link back); `onRetry` adds a
 * "Try again" button for load failures.
 */
export function Empty({ icon = 'sparkle', title, children, action, onRetry }: {
  icon?: string;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  onRetry?: (() => void) | undefined;
}): ReactNode {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} size={22} />
      </span>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
      {(action || onRetry) && (
        <div className="empty-actions">
          {onRetry && (
            <button type="button" className="btn btn-sm" onClick={onRetry} data-testid="empty-retry">
              <Icon name="refresh" size={16} /> Try again
            </button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}
